import { Component, ErrorInfo, ReactNode } from "react";
import { reportClientError } from "@/api/clientErrors";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary global (regla #32 del prompt maestro).
 * Ningún error de renderizado debe dejar al usuario ante una pantalla blanca.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary capturó un error:", error, errorInfo);
    reportClientError(error, errorInfo.componentStack ?? undefined);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = (): void => {
    window.location.href = "/";
  };

  private handleReport = (): void => {
    const subject = encodeURIComponent("Reporte de error - ERP Firma Profesional");
    const body = encodeURIComponent(
      `Se produjo un error inesperado.\n\nMensaje: ${this.state.error?.message ?? "desconocido"}\nURL: ${window.location.href}`
    );
    window.location.href = `mailto:soporte@example.com?subject=${subject}&body=${body}`;
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="card max-w-md w-full p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <span className="text-2xl">⚠️</span>
            </div>
            <h1 className="text-lg font-semibold text-gray-900">Ha ocurrido un error</h1>
            <p className="mt-2 text-sm text-gray-500">
              Algo salió mal al procesar esta pantalla. Puede intentar de nuevo o volver al inicio.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button type="button" className="btn-primary" onClick={this.handleRetry}>
                Reintentar
              </button>
              <button type="button" className="btn-secondary" onClick={this.handleGoHome}>
                Volver al inicio
              </button>
              <button type="button" className="btn-secondary" onClick={this.handleReport}>
                Reportar problema
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
